import { useEffect, useMemo, useState, useCallback } from "react";
import { Button, Card } from "antd";
import {
  ResponsiveContainer,
  CartesianGrid,
  Tooltip,
  XAxis,
  YAxis,
  ComposedChart,
  Bar,
  Line,
} from "recharts";
import { parseISO, format } from "date-fns";
import styles from "./styles.module.scss";
import Setting from "@assets/icons/setting";
import DaysSelect from "@pages/AdsAnalitics/components/Header/ui/CustomDays";
import { CustomTooltip } from "@pages/Dashboard/features/SalesPerformance/Chart/CustomTooltip";
import { SettingsModal } from "./modal";
import PoSalesTable from "../PoSalesTable";
import { legendColorMapping } from "./utils";
import { useDashboardStore } from "@pages/Dashboard/store/dashboard.state";
import { DatePeriod } from "@services/inventory-performance/inventory-performance.api";
import {
  InventoryMetricsApi,
  APIMetricsResponse,
  APIMetricsItem,
} from "@services/inventory/inventory.api";
import { modifyTooltipPayload } from "@components/SalesPerformance/utils";
import classNames from "classnames";

const ALL_KEYS: Array<keyof APIMetricsItem> = [
  "available_stock",
  "in_transit",
  "receiving",
  "safety_stock",
];

export const LABEL_MAPPING: Record<string, string> = {
  available_stock: "Available Stock",
  in_transit: "In Transit",
  receiving: "Receiving",
  safety_stock: "Safety Stock",
};

const InventoryPerformance = ({
  title,
  isCash = false,
  hideBg = false,
}: {
  title?: string;
  isCash?: boolean;
  hideBg?: boolean;
}) => {
  const [dataPoints, setDataPoints] = useState<Record<string, boolean>>({
    available_stock: true,
    in_transit: true,
    receiving: true,
    safety_stock: true,
  });
  const [showForecast, setShowForecast] = useState(false);
  const [isPoModal, setIsPoModal] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [data, setData] = useState<APIMetricsItem[]>([]);
  const [period, setPeriod] = useState<DatePeriod>("DAY");
  const [hasSafetyStockFromApi, setHasSafetyStockFromApi] = useState(false);

  const inventoryApi = useMemo(() => new InventoryMetricsApi(), []);

  const {
    isAsinSelect,
    asinSelected,
    brandSelected,
    isBrandSelect,
    dateRange,
  } = useDashboardStore((state) => ({
    isAsinSelect: state.isAsinSelect,
    asinSelected: state.asinSelected,
    brandSelected: state.brandSelected,
    isBrandSelect: state.isBrandSelect,
    dateRange: state.dateRange,
  }));

  const hasNonEmptyStringOrArray = useCallback(
    (value: string | string[] | undefined): boolean => {
      if (typeof value === "string") {
        return value.trim() !== "";
      } else if (Array.isArray(value)) {
        return value.length > 0;
      } else {
        return false;
      }
    },
    [],
  );

  const fetchData = useCallback(async () => {
    if (!dateRange.startDate || !dateRange.endDate) return;

    try {
      let apiResponse: APIMetricsResponse;

      if (isBrandSelect) {
        if (hasNonEmptyStringOrArray(brandSelected)) {
          apiResponse = await inventoryApi.getInventoryByBrand(
            dateRange.startDate,
            dateRange.endDate,
            brandSelected,
          );
        } else {
          apiResponse = await inventoryApi.getInventoryByBrand(
            dateRange.startDate,
            dateRange.endDate,
          );
        }
      } else if (isAsinSelect) {
        if (hasNonEmptyStringOrArray(asinSelected)) {
          apiResponse = await inventoryApi.getInventory(
            dateRange.startDate,
            dateRange.endDate,
            asinSelected,
          );
        } else {
          apiResponse = await inventoryApi.getInventory(
            dateRange.startDate,
            dateRange.endDate,
          );
        }
      } else {
        apiResponse = await inventoryApi.getInventory(
          dateRange.startDate,
          dateRange.endDate,
        );
      }

      const metrics = apiResponse.daily_metrics || [];
      setData(metrics);
      const hasSS = metrics.some(
        (item) => item.safety_stock != null && item.safety_stock !== 0,
      );
      setHasSafetyStockFromApi(hasSS);
    } catch {
      setData([]);
      setHasSafetyStockFromApi(false);
    }
  }, [
    dateRange.startDate,
    dateRange.endDate,
    isAsinSelect,
    asinSelected,
    isBrandSelect,
    brandSelected,
    inventoryApi,
    hasNonEmptyStringOrArray,
  ]);

  useEffect(() => {
    if (title) return;
    fetchData();
  }, [title, fetchData, period, asinSelected, brandSelected, isBrandSelect]);

  const usedKeys = ALL_KEYS.filter((key) => dataPoints[key]);

  const processedData = useMemo(
    () =>
      data.map((item) => {
        const formattedDate = format(parseISO(item.date), "dd MMM");
        const obj: Record<string, any> = { date: formattedDate };
        usedKeys.forEach((key) => {
          obj[`${key}_main`] = item[key] ?? 0;
        });
        return obj;
      }),
    [data, usedKeys],
  );

  const renderTooltip = useCallback(
    ({ active, payload, label }: any) =>
      active ? (
        <CustomTooltip
          active={active}
          payload={modifyTooltipPayload(payload)}
          colorMetric={legendColorMapping}
          label={label}
        />
      ) : null,
    [],
  );

  const handleSelect = useCallback((value) => {
    if (value === "Days") setPeriod("DAY");
    else if (value === "Week") setPeriod("WEEK");
    else setPeriod("MONTH");
  }, []);

  const options = ["Days", "Week", "Month"];

  return (
    <Card
      className={classNames(styles.card, { [styles.noBackground]: hideBg })}
    >
      <div className={styles.headerBetween}>
        <h1 className={styles.title}>
          {title ||
            (isPoModal
              ? "PO Sales, Margin & ROI Dynamics"
              : "Inventory Performance")}
        </h1>
        {title ? (
          <DaysSelect
            options={options}
            defaultLabel="Week View"
            onSelect={handleSelect}
          />
        ) : (
          <Button
            type="text"
            icon={<Setting />}
            onClick={() => setIsOpenModal(true)}
          />
        )}
      </div>

      <div className={styles.legendContainer}>
        {usedKeys.map((key) => (
          <div className={styles.legendItem} key={key}>
            <span
              className={styles.colorDot}
              style={{ backgroundColor: legendColorMapping[key] || "#5C5C5A" }}
            />
            <span className={styles.legendText}>{LABEL_MAPPING[key]}</span>
          </div>
        ))}
      </div>

      {isPoModal || isCash ? (
        <div className={styles.tableContainer}>
          <PoSalesTable />
        </div>
      ) : (
        <div className={styles.chartContainer}>
          <ResponsiveContainer width="100%" height={350}>
            <ComposedChart
              data={processedData}
              margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
            >
              <CartesianGrid stroke="#1F1F20" vertical={false} />
              <YAxis
                yAxisId="left"
                tick={{ fill: "white" }}
                axisLine={{ stroke: "#1F1F20" }}
                domain={[0, "dataMax + 50"]}
              />
              <YAxis yAxisId="right" hide />
              <XAxis
                dataKey="date"
                tick={{ fill: "white" }}
                axisLine={{ stroke: "#1F1F20" }}
              />
              <Tooltip
                content={renderTooltip}
                cursor={{ fill: "transparent" }}
              />

              {usedKeys
                .filter((key) => key !== "safety_stock")
                .map((key) => (
                  <Bar
                    key={key}
                    yAxisId="left"
                    dataKey={`${key}_main`}
                    stackId="a"
                    name={LABEL_MAPPING[key]}
                    fill={legendColorMapping[key] || "#5C5C5A"}
                    barSize={55}
                  />
                ))}

              {hasSafetyStockFromApi && dataPoints.safety_stock && (
                <Line
                  yAxisId="left"
                  type="basis"
                  dataKey="safety_stock_main"
                  name={LABEL_MAPPING["safety_stock"]}
                  stroke={legendColorMapping["safety_stock"] || "#5C5C5A"}
                  dot={false}
                  strokeWidth={4}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}

      <SettingsModal
        open={isOpenModal}
        onClose={() => setIsOpenModal(false)}
        setDataPoints={setDataPoints}
        dataPoints={dataPoints}
        showForecast={showForecast}
        setShowForecast={setShowForecast}
        isPoModal={isPoModal}
        setIsPoModal={setIsPoModal}
        isInventory
      />
    </Card>
  );
};

export default InventoryPerformance;
